/**
 * @therich-team/design-system v0.1.36 의 토큰을 그대로 내재화한 것.
 * 값·순서를 바꾸면 기존 화면 치수가 달라지므로 수정 금지.
 */
export const designToken = {
  fontFamilies: { 'apple-sd-gothic-neo': 'Apple SD Gothic Neo' },
  lineHeights: { '140': '140%', '150': '150%' },
  fontWeights: { regular: 'Regular', medium: 'Medium', bold: 'Bold' },
} as const

export const breakpointsToken = {
  mobile: 0,
  labtop: 767,
} as const

export const breakpoints = {
  mobile: `@media (max-width: ${breakpointsToken.labtop}px)`,
  desktop: `@media (min-width: ${breakpointsToken.labtop + 1}px)`,
}

export const spacingToken = {
  withVar: {
    '100': 'var(--spacing-100)',
    '200': 'var(--spacing-200)',
    '300': 'var(--spacing-300)',
    '400': 'var(--spacing-400)',
    '500': 'var(--spacing-500)',
    '600': 'var(--spacing-600)',
    '700': 'var(--spacing-700)',
    '800': 'var(--spacing-800)',
    '900': 'var(--spacing-900)',
    '1000': 'var(--spacing-1000)',
    '1100': 'var(--spacing-1100)',
    '1200': 'var(--spacing-1200)',
    '1300': 'var(--spacing-1300)',
    '1400': 'var(--spacing-1400)',
    '1500': 'var(--spacing-1500)',
  },
  css: {
    desktop:
      '--spacing-100: 0.4rem;--spacing-200: 0.8rem;--spacing-300: 1.2rem;--spacing-400: 1.6rem;--spacing-500: 2rem;--spacing-600: 2.4rem;--spacing-700: 2.8rem;--spacing-800: 3.2rem;--spacing-900: 3.6rem;--spacing-1000: 4rem;--spacing-1100: 4.4rem;--spacing-1200: 4.8rem;--spacing-1300: 5.2rem;--spacing-1400: 5.6rem;--spacing-1500: 6rem;',
    mobile:
      '--spacing-100: 1.0666666666666667vw;--spacing-200: 2.1333333333333333vw;--spacing-300: 3.2vw;--spacing-400: 4.266666666666667vw;--spacing-500: 5.333333333333334vw;--spacing-600: 6.4vw;--spacing-700: 7.466666666666668vw;--spacing-800: 8.533333333333333vw;--spacing-900: 9.6vw;--spacing-1000: 10.666666666666668vw;--spacing-1100: 11.733333333333333vw;--spacing-1200: 12.8vw;--spacing-1300: 13.866666666666665vw;--spacing-1400: 14.933333333333335vw;--spacing-1500: 16vw;',
  },
} as const

export const fontsizeToken = {
  withVar: {
    body5: 'var(--fontsize-body5)',
    body4: 'var(--fontsize-body4)',
    body3: 'var(--fontsize-body3)',
    body2: 'var(--fontsize-body2)',
    body1: 'var(--fontsize-body1)',
    h7: 'var(--fontsize-h7)',
    h6: 'var(--fontsize-h6)',
    h5: 'var(--fontsize-h5)',
    h4: 'var(--fontsize-h4)',
    h3: 'var(--fontsize-h3)',
    h2: 'var(--fontsize-h2)',
    h1: 'var(--fontsize-h1)',
  },
  css: {
    desktop:
      '--fontsize-body5: 1rem;--fontsize-body4: 1.2rem;--fontsize-body3: 1.4rem;--fontsize-body2: 1.5rem;--fontsize-body1: 1.6rem;--fontsize-h7: 1.7rem;--fontsize-h6: 1.8rem;--fontsize-h5: 2rem;--fontsize-h4: 2.2rem;--fontsize-h3: 2.4rem;--fontsize-h2: 2.8rem;--fontsize-h1: 4rem;',
    mobile:
      '--fontsize-body5: 2.666666666666667vw;--fontsize-body4: 3.2vw;--fontsize-body3: 3.733333333333334vw;--fontsize-body2: 4vw;--fontsize-body1: 4.266666666666667vw;--fontsize-h7: 4.533333333333333vw;--fontsize-h6: 4.8vw;--fontsize-h5: 5.333333333333334vw;--fontsize-h4: 5.866666666666666vw;--fontsize-h3: 6.4vw;--fontsize-h2: 7.466666666666668vw;--fontsize-h1: 10.666666666666668vw;',
  },
} as const

export const colorToken = {
  withVar: {
    primary: {
      yellow100: 'var(--primary-yellow100)',
      yellow50: 'var(--primary-yellow50)',
      yellow20: 'var(--primary-yellow20)',
      yellow15: 'var(--primary-yellow15)',
      yellow10: 'var(--primary-yellow10)',
      navy900: 'var(--primary-navy900)',
      navy800: 'var(--primary-navy800)',
      navy700: 'var(--primary-navy700)',
      navy600: 'var(--primary-navy600)',
      navy500: 'var(--primary-navy500)',
      navy400: 'var(--primary-navy400)',
    },
    grayscale: {
      gray900: 'var(--grayscale-gray900)',
      gray800: 'var(--grayscale-gray800)',
      gray600: 'var(--grayscale-gray600)',
      gray500: 'var(--grayscale-gray500)',
      gray400: 'var(--grayscale-gray400)',
      gray50: 'var(--grayscale-gray50)',
    },
  },
  css: {
    dark: '--primary-yellow100: #f1b13c;--primary-yellow50: #82642f;--primary-yellow20: #3f3627;--primary-yellow15: #342e26;--primary-yellow10: #292625;--primary-navy900: #0f131c;--primary-navy800: #131722;--primary-navy700: #1b1f2c;--primary-navy600: #262936;--primary-navy500: #30333f;--primary-navy400: #64666e;--grayscale-gray900: #212121;--grayscale-gray800: #505050;--grayscale-gray600: #757575;--grayscale-gray500: #9e9e9e;--grayscale-gray400: #bdbdbd;--grayscale-gray50: #fafafa;--secondary-brightRed100: #ff453a;--secondary-brightRed50: #892e2e;--secondary-brightRed15: #361e26;--secondary-darkRed100: #c1464e;--secondary-darkRed50: #6a2e38;--secondary-darkRed15: #2d1e29;--secondary-emerald100: #45c9ab;--secondary-emerald50: #2c7066;--secondary-emerald15: #1b3237;--secondary-green100: #31e038;--secondary-green50: #227c2d;--secondary-green15: #183525;--secondary-blue100: #0383fa;--secondary-blue50: #0b4d8e;--secondary-blue15: #112742;--secondary-black100: #000000;--secondary-black80: #000000cc;--secondary-white100: #ffffff;--secondary-white80: #ffffffcc;--chart-1: #079992;--chart-2: #0a3d62;--chart-3: #0c2461;--chart-4: #b71540;--chart-5: #e58e26;--chart-6: #38ada9;--chart-7: #3c6382;--chart-8: #187abd;--chart-9: #e55039;--chart-10: #f6b93b;--chart-11: #78e08f;--chart-12: #60a3bc;--chart-13: #fad390;--chart-14: #6a89cc;--chart-15: #b8e994;--chart-16: #a0a0a0;--gradient-greenBlue: linear-gradient(#2db48b,#1B67BF);--gradient-purpleBlue: linear-gradient(#6c2ab1,#399ede);--gradient-pinkRed: linear-gradient(#A540F9, #ba1641);--gradient-orangeYellow: linear-gradient(#CA4938,#e3b456);--gradient-yellowOrange: linear-gradient(#e3b456, #CA4938);--gradient-brightDarkNavy: linear-gradient(#1f273a, #1B1F2C);--gradient-tangerineYellow: linear-gradient(#fb992a, #FDC954);--gradientColor-green: #2db48b;--gradientColor-darkBlue: #1B67BF;--gradientColor-purple: #6c2ab1;--gradientColor-blue: #399ede;--gradientColor-pink: #A540F9;--gradientColor-red: #ba1641;--gradientColor-darkYellow: #e3b456;--gradientColor-orange: #CA4938;--gradientColor-brightDark: #1f273a;--gradientColor-navy: #1B1F2C;--gradientColor-tangerine: #fb992a;--gradientColor-lightYellow: #FDC954;',
    light:
      '--primary-yellow100: #ffffff;--primary-yellow50: #ffffff;--primary-yellow20: #ffffff;--primary-yellow15: #ffffff;--primary-yellow10: #ffffff;--primary-navy900: #ffffff;--primary-navy800: #ffffff;--primary-navy700: #ffffff;--primary-navy600: #ffffff;--primary-navy500: #ffffff;--primary-navy400: #ffffff;--grayscale-gray900: #ffffff;--grayscale-gray800: #ffffff;--grayscale-gray600: #ffffff;--grayscale-gray500: #ffffff;--grayscale-gray400: #ffffff;--grayscale-gray50: #ffffff;--secondary-brightRed100: #ffffff;--secondary-brightRed50: #ffffff;--secondary-brightRed15: #ffffff;--secondary-darkRed100: #ffffff;--secondary-darkRed50: #ffffff;--secondary-darkRed15: #ffffff;--secondary-emerald100: #ffffff;--secondary-emerald50: #ffffff;--secondary-emerald15: #ffffff;--secondary-green100: #ffffff;--secondary-green50: #ffffff;--secondary-green15: #ffffff;--secondary-blue100: #ffffff;--secondary-blue50: #ffffff;--secondary-blue15: #ffffff;--secondary-black100: #ffffff;--secondary-black80: #ffffff;--secondary-white100: #ffffff;--secondary-white80: #ffffff;--chart-1: #ffffff;--chart-2: #ffffff;--chart-3: #ffffff;--chart-4: #ffffff;--chart-5: #ffffff;--chart-6: #ffffff;--chart-7: #ffffff;--chart-8: #ffffff;--chart-9: #ffffff;--chart-10: #ffffff;--chart-11: #ffffff;--chart-12: #ffffff;--chart-13: #ffffff;--chart-14: #ffffff;--chart-15: #ffffff;--chart-16: #ffffff;--gradient-greenBlue: #ffffff;--gradient-purpleBlue: #ffffff;--gradient-pinkRed: #ffffff;--gradient-orangeYellow: #ffffff;--gradient-yellowOrange: #ffffff;--gradient-brightDarkNavy: #ffffff;--gradient-tangerineYellow: #ffffff;--gradientColor-green: #ffffff;--gradientColor-darkBlue: #ffffff;--gradientColor-purple: #ffffff;--gradientColor-blue: #ffffff;--gradientColor-pink: #ffffff;--gradientColor-red: #ffffff;--gradientColor-darkYellow: #ffffff;--gradientColor-orange: #ffffff;--gradientColor-brightDark: #ffffff;--gradientColor-navy: #ffffff;--gradientColor-tangerine: #ffffff;--gradientColor-lightYellow: #ffffff;',
  },
} as const

const sizeByDeviceWidth = (width: number) => {
  return (pixel: number, unit = 'vw') => {
    return `${(pixel / width) * 100}${unit}`
  }
}

export const pixelToVwBy375 = sizeByDeviceWidth(375)
